import { api } from '../console/api.ts';
import { carrierName } from '../console/carriers.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { esc, icon, initials, PRODUCT, person, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { act } from '../console/ui.ts';
import { selectControl, textControl } from '../controls.ts';

/**
 * «Antecedentes»: the evaluator's queue of candidates waiting for the background verdict. It
 * keeps nothing of its own between screens (no reset).
 */

export type BackgroundView = {
  id: string;
  candidate: { fullName: string; email: string; phone: string | null; source: string };
  carrier: string;
  productLines: string[];
  status: string;
  backgroundCheck: {
    status: string;
    proposal: {
      suggested: string;
      findings: string[];
      reference: string;
      at: string;
      by: string;
    } | null;
    assessments: { verdict: string; note: string | null; at: string; by: string }[];
  };
};
/** The evaluator's verdicts in the client's words, with the pill that carries each one. */
export const VERDICT: Record<string, [string, string]> = {
  APPROVED: ['ok', 'Aprobado'],
  REJECTED: ['bad', 'Rechazado'],
  NEEDS_REVIEW: ['warn', 'Requiere revisión'],
};
export const verdictPill = (v: string) => {
  const [cls, label] = VERDICT[v] ?? ['info', v];
  return pill(cls, label);
};

/**
 * The evaluator's queue: candidates who sent their application and wait for the background
 * verdict. The lists-and-bureau check only suggests; the evaluator decides. The direction and
 * administration see the same queue without the verdict form.
 */
export async function antecedentes() {
  const queue = await api<BackgroundView[]>('GET', '/v1/background-checks');
  const judge = has('evaluator');
  let html = pageHead(
    'Verificación de antecedentes',
    judge
      ? 'Candidatos que enviaron su boleta y esperan tu dictamen. Consulta listas y buró: el resultado es una sugerencia; la decisión es tuya.'
      : 'Candidatos que esperan el dictamen de la evaluación. La dirección recibe un aviso con cada dictamen.',
  );
  const last = (o: BackgroundView) => o.backgroundCheck.assessments.at(-1);
  const inReview = queue.filter((o) => last(o)?.verdict === 'NEEDS_REVIEW').length;
  html += `<section class="kpis" aria-label="Antecedentes">
    <div class="card kpi"><span class="kpi-label">${icon('inbox')}Pendientes</span><span class="kpi-value">${queue.length}</span><span class="kpi-foot">esperan dictamen</span></div>
    <div class="card kpi ${inReview ? 'warn' : ''}"><span class="kpi-label">${icon('warn')}En revisión</span><span class="kpi-value">${inReview}</span><span class="kpi-foot">con «Requiere revisión»</span></div>
  </section>`;
  if (!queue.length)
    return (
      html +
      empty(
        'shield',
        'No hay expedientes pendientes',
        'Cuando un candidato envíe su boleta, aparecerá aquí para verificar sus antecedentes.',
        '',
        2,
      )
    );
  const cards = queue
    .map((o) => {
      const bc = o.backgroundCheck;
      const p = bc.proposal;
      const latest = last(o);
      const proposal = p
        ? `<p class="note ${p.suggested === 'APPROVED' ? 'info' : p.suggested === 'REJECTED' ? 'bad' : ''}">${icon(p.suggested === 'APPROVED' ? 'ok' : 'alert')}<span>Sugerencia de listas y buró: <b>${esc(VERDICT[p.suggested]?.[1] ?? p.suggested)}</b> · ${p.findings.map((f) => esc(f)).join(' · ')} <span class="muted small">(${esc(p.reference)}, ${esc(when(p.at))})</span></span></p>`
        : '';
      const history = bc.assessments.length
        ? `<ol class="steps">${bc.assessments
            .map(
              (a) =>
                `<li class="step"><div class="step-head"><strong>Dictamen</strong>${verdictPill(a.verdict)}</div><span class="muted small">${esc(when(a.at))} · ${esc(person(a.by))}</span>${a.note ? `<span class="timeline-note">${esc(a.note)}</span>` : ''}</li>`,
            )
            .join('')}</ol>`
        : '';
      const form = judge
        ? `<div class="toolbar order-actions" data-bg-form="${esc(o.id)}">
          ${field(
            'Dictamen',
            selectControl({ 'data-bg-verdict': true }, [
              { value: 'APPROVED', label: 'Aprobado' },
              { value: 'NEEDS_REVIEW', label: 'Requiere revisión' },
              { value: 'REJECTED', label: 'Rechazado' },
            ]),
          )}
          ${field('Notas', textControl({ 'data-bg-note': true, maxlength: 500, placeholder: 'Obligatorias si rechazas o pides revisión' }))}
          <div class="order-buttons"><button type="button" class="btn sm" data-bg-propose="${esc(o.id)}">Consultar listas y buró</button>
          <button type="button" class="btn sm primary" data-bg-assess="${esc(o.id)}">Emitir dictamen</button></div></div>`
        : '';
      return `<article class="card"><div class="card-head">
        <span class="avatar onb-avatar" aria-hidden="true">${esc(initials(o.candidate.fullName))}</span>
        <div class="policy-title"><strong>${esc(o.candidate.fullName)}</strong>
          <div class="policy-meta"><span>${esc(carrierName(o.carrier))}</span><span>${o.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ')}</span><span>${esc(o.candidate.source)}</span><span>${esc(o.candidate.email)}</span></div></div>
        <span class="spacer"></span>${latest ? verdictPill(latest.verdict) : pill('info', 'Por dictaminar')}</div>
        ${proposal}${history}${form}</article>`;
    })
    .join('');
  return `${html}<section class="policies">${cards}</section>`;
}

export const actions: Actions = {
  click: {
    bgPropose: (t, d) => {
      if (!d.bgPropose) return;
      const id = d.bgPropose;
      void act(t, 'Consultando listas y buró…', async () => {
        const o = await api<BackgroundView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(id)}/background-check/proposal`,
          {},
        );
        await render(current, { quiet: true });
        const s = o.backgroundCheck.proposal?.suggested ?? '';
        return `Sugerencia para ${o.candidate.fullName}: ${VERDICT[s]?.[1] ?? s}. El dictamen es tuyo.`;
      });
    },
    bgAssess: (t, d) => {
      if (!d.bgAssess) return;
      const id = d.bgAssess;
      const box = document.querySelector<HTMLElement>(`[data-bg-form="${CSS.escape(id)}"]`);
      const verdict = box?.querySelector<HTMLSelectElement>('[data-bg-verdict]')?.value ?? '';
      const note = box?.querySelector<HTMLInputElement>('[data-bg-note]')?.value.trim() ?? '';
      void act(t, 'Registrando el dictamen…', async () => {
        if (verdict === 'REJECTED' && !note) throw new InputError('Escribe el motivo del rechazo.');
        if (verdict === 'NEEDS_REVIEW' && !note)
          throw new InputError('Escribe qué hay que revisar.');
        const o = await api<BackgroundView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(id)}/background-check`,
          { verdict, ...(note ? { note } : {}) },
        );
        await render(current, { quiet: true });
        return verdict === 'APPROVED'
          ? `Antecedentes de ${o.candidate.fullName} aprobados; la dirección recibió el aviso.`
          : verdict === 'REJECTED'
            ? `${o.candidate.fullName}: rechazado; el onboarding quedó cerrado.`
            : `${o.candidate.fullName} queda en revisión; la dirección recibió el aviso.`;
      });
    },
  },
};
